'use client';

import * as React from 'react';
import { Package, Zap } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/ui/page-header';
import { TableCard } from '@/components/ui/table-card';
import { useStoresList } from '@/lib/api/stores';
import { StoresTable } from './components/StoresTable';
import { storesFilterMapping, storesSortMapping, storesQueryParamsMapping } from './utils/table-mappings';
import { Link, useNavigate } from 'react-router-dom';
import { useOrgNavigate } from '@/hooks/use-app-navigate';
import { useTablePage } from '@/hooks/use-table-page';
import { useOnboarding } from '@/hooks/use-onboarding';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useOrganization } from '@clerk/clerk-react';

import { withRequiredPermission } from '@/components/auth/with-required-permission';
import { PERMISSIONS } from '@/hooks/use-perm';

export default withRequiredPermission(StoresPage, PERMISSIONS.STORES_VIEW);

function StoresPage() {
  const { t } = useTranslation(['stores', 'common']);
  const { orgRoutes } = useOrgNavigate();
  const navigate = useNavigate();
  const { organization } = useOrganization();
  const { state: onboardingState, intentType, pluginName, complete } = useOnboarding();

  const { setTableState, queryInput } = useTablePage({
    initialSorting: [{ id: 'createdAt', desc: true }],
    filterMapping: storesFilterMapping,
    sortMapping: storesSortMapping,
    queryParamsMapping: storesQueryParamsMapping,
  });

  const {
    data: storesResponse,
    isLoading: loading,
    isFetching: fetching,
    error,
    refetch,
  } = useStoresList({
    search: queryInput.search,
    pagination: queryInput.pagination,
    sortBy: queryInput.sortBy,
    filters: queryInput.filters,
  });

  const stores = storesResponse?.stores || [];
  const total = storesResponse?.total || 0;

  // Handle store selection for onboarding
  // Note: Users can click on stores through the table's built-in navigation
  // When they return or create a new store, the onboarding flow will continue automatically

  // Auto-redirect if user has stores and is in onboarding mode
  React.useEffect(() => {
    if (onboardingState?.intent && organization?.slug && stores.length > 0 && !loading) {
      // Use the first store for onboarding continuation
      const firstStore = stores[0];
      const redirectUrl = complete(organization.slug, firstStore.id);

      // Small delay to let user see the stores list
      const timer = setTimeout(() => {
        navigate(redirectUrl);
      }, 1500);

      return () => clearTimeout(timer);
    }
  }, [onboardingState, organization, stores, loading, complete, navigate]);

  const getPluginInstallMessage = () => ({
    title: t('stores:onboarding.pluginInstall.title', { pluginName: pluginName || 'this plugin' }),
    description: stores.length > 0
      ? t('stores:onboarding.pluginInstall.descriptionWithStores', { pluginName: pluginName || 'this plugin' })
      : t('stores:onboarding.pluginInstall.descriptionWithoutStores', { pluginName: pluginName || 'this plugin' }),
    icon: <Zap className="h-5 w-5 text-emerald-600" />,
  });

  const getFunnelCreateMessage = () => ({
    title: t('stores:onboarding.funnelCreate.title'),
    description: stores.length > 0
      ? t('stores:onboarding.funnelCreate.descriptionWithStores')
      : t('stores:onboarding.funnelCreate.descriptionWithoutStores'),
    icon: <Zap className="h-5 w-5 text-emerald-600" />,
  });

  const getIntentMessage = () => {
    if (!onboardingState?.intent) return null;
    if (intentType === 'plugin_install') return getPluginInstallMessage();
    if (intentType === 'funnel_create') return getFunnelCreateMessage();
    return null;
  };

  const intentMessage = getIntentMessage();

  return (
    <div className="flex flex-col space-y-4 sm:space-y-6">
      {/* Show context banner if there's an onboarding intent */}
      {intentMessage && (
        <Card className="border-emerald-200 bg-emerald-50/50">
          <CardHeader>
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-emerald-100">
                {intentMessage.icon}
              </div>
              <div className="flex-1">
                <CardTitle className="text-lg">{intentMessage.title}</CardTitle>
                <CardDescription className="mt-1 text-sm">{intentMessage.description}</CardDescription>
              </div>
            </div>
          </CardHeader>
          {stores.length > 0 && (
            <CardContent>
              <p className="text-muted-foreground text-sm">
                {t('stores:onboarding.selectStoreHint')}
              </p>
            </CardContent>
          )}
        </Card>
      )}

      {/* Page header */}
      <PageHeader title={t('stores:title')} description={t('stores:description')}>
        <Link to={orgRoutes.STORE_CREATE()}>
          <Button className="w-full sm:w-auto">
            <Package className="size-4" />
            {t('stores:addStore')}
          </Button>
        </Link>
      </PageHeader>

      {/* Stores table */}
      <TableCard>
        <StoresTable
          stores={stores}
          total={total}
          loading={loading || fetching}
          onTableStateChange={setTableState}
          onRefetch={refetch}
        />

        {/* Error message */}
        {error && (
          <div className="mt-4 rounded border border-red-400 bg-red-100 px-4 py-3 text-red-700">
            {error instanceof Error ? error.message : t('stores:loadError')}
          </div>
        )}
      </TableCard>
    </div>
  );
}
